"use client";

import React from "react";
import { SectionContentProps } from "../../types";
import { FooterContent, SocialLink, LinkGroup } from "../../sections/footer/types";
import { SocialLinks, Copyright } from "../../sections/footer/BaseFooter";
import { PiEnvelopeSimple } from "react-icons/pi";
import EditableText from "../../core/EditableText";
import LogoUploader from "../../sections/header/LogoUploader";

interface HokFooterProps extends SectionContentProps {
  content: FooterContent;
  defaultLogo?: string;
}

export const HokFooter: React.FC<HokFooterProps> = ({
  content: propContent,
  onContentChange,
  isSelected,
  defaultLogo,
}) => {
  const content = propContent || {};
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const primaryColor = content.accentColor || content.colorPalette?.primary || "#684DF4";
  const secondaryColor = content.colorPalette?.secondary || "#8a2be2";

  const backgroundImage = content.backgroundImage || "/assets/img/bg/footer_bg_1.jpg";

  const defaultSocialLinks: SocialLink[] = [
    { platform: "facebook", url: "https://www.facebook.com/" },
    { platform: "twitter", url: "https://www.twitter.com/" },
    { platform: "linkedin", url: "https://www.linkedin.com/" },
    { platform: "instagram", url: "https://www.instagram.com/" },
  ];

  const defaultLinkGroups: LinkGroup[] = [
    {
      title: "Quick Links",
      links: [
        { label: "All Products", href: "#" },
        { label: "Product Catalog", href: "shop.html" },
        { label: "Voucher Card", href: "#" },
        { label: "Report Abuse", href: "contact.html" },
        { label: "Departments", href: "contact.html" },
        { label: "Policies & Rules", href: "contact.html" },
      ],
    },
    {
      title: "Resources",
      links: [
        { label: "Blog", href: "contact.html" },
        { label: "About Us", href: "contact.html" },
        { label: "How to Purchase", href: "contact.html" },
        { label: "Affiliate", href: "contact.html" },
        { label: "Contact Us", href: "contact.html" },
        { label: "Community", href: "contact.html" },
      ],
    },
  ];

  const socialLinks: SocialLink[] = content.socialLinks || defaultSocialLinks;
  const linkGroups: LinkGroup[] = content.linkGroups || defaultLinkGroups;

  const updateField = (field: string, value: any) => {
    if (!onContentChange) return;
    onContentChange({ ...propContent, [field]: value });
  };

  const handleLogoChange = (newLogoUrl: string) => {
    updateField("logo", newLogoUrl);
  };

  return (
    <footer
      className={`relative overflow-hidden ${isSelected ? "ring-2 ring-blue-500" : ""}`}
      style={{
        fontFamily,
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      role="contentinfo"
      aria-label="Hok footer"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${primaryColor}95, ${secondaryColor}95)`,
        }}
      />

      <div className="relative z-10">
        {/* Widget Area */}
        <div className="py-12 sm:py-16 px-4 sm:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
              {/* Column 1: Logo & About */}
              <div className="lg:col-span-1">
                <div className="mb-6">
                  <LogoUploader
                    currentLogo={content.logo}
                    defaultLogo={defaultLogo}
                    onLogoChange={handleLogoChange}
                    heightClass="h-10"
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    companyName={content.companyName || "HOK"}
                    isEditing={!!onContentChange}
                  />
                  <p className="text-white/80 text-sm leading-relaxed mb-6">
                    <EditableText
                      value={content.description || "At Instacks, we empower learners and instructors to succeed. Our platform offers quality courses, expert instructors, and a supportive community for lifelong learning."}
                      onChange={(val) => updateField("description", val)}
                      as="span"
                      placeholder="About text..."
                    />
                  </p>
                </div>

                {/* Social Links */}
                <div className="flex flex-wrap gap-3">
                  {socialLinks.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                      title={link.platform}
                    >
                      <SocialIcon platform={link.platform} />
                    </a>
                  ))}
                </div>
              </div>

              {/* Columns 2-3: Link Groups */}
              {linkGroups.slice(0, 2).map((group, groupIdx) => (
                <div key={groupIdx}>
                  <h3 className="text-white font-bold text-base mb-4">
                    <EditableText
                      value={group.title}
                      onChange={(val) => {
                        if (!onContentChange) return;
                        const newGroups = [...linkGroups];
                        newGroups[groupIdx] = { ...newGroups[groupIdx], title: val };
                        updateField("linkGroups", newGroups);
                      }}
                      as="span"
                      placeholder="Column Title"
                    />
                  </h3>
                  <ul className="space-y-2">
                    {group.links.map((link, linkIdx) => (
                      <li key={linkIdx}>
                        <a
                          href={link.href}
                          className="text-white/70 hover:text-white text-sm transition-colors"
                        >
                          <EditableText
                            value={link.label}
                            onChange={(val) => {
                              if (!onContentChange) return;
                              const newGroups = [...linkGroups];
                              const newLinks = [...newGroups[groupIdx].links];
                              newLinks[linkIdx] = { ...newLinks[linkIdx], label: val };
                              newGroups[groupIdx] = { ...newGroups[groupIdx], links: newLinks };
                              updateField("linkGroups", newGroups);
                            }}
                            as="span"
                            placeholder="Link Label"
                          />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

            </div>
          </div>
        </div>

        {/* Copyright Bar */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/60">
              <p>
                <EditableText
                  value={content.copyrightText || `© ${new Date().getFullYear()} ${content.companyName || "Instacks"}. All rights reserved.`}
                  onChange={(val) => updateField("copyrightText", val)}
                  as="span"
                  placeholder="Copyright text"
                />
              </p>

              <div className="flex items-center gap-6">
                {(content.footerLinks || [
                  { label: "Support Center", href: "#" },
                  { label: "Terms of Service", href: "#" },
                  { label: "Privacy Policy", href: "#" }
                ]).map((link: any, idx: number) => (
                  <a
                    key={idx}
                    href={onContentChange ? undefined : link.href}
                    onClick={(e) => { if (onContentChange) e.preventDefault(); }}
                    className="hover:text-white transition-colors"
                  >
                    <EditableText
                      value={link.label}
                      onChange={(val) => {
                        if (!onContentChange) return;
                        const newLinks = [...(content.footerLinks || [
                          { label: "Support Center", href: "#" },
                          { label: "Terms of Service", href: "#" },
                          { label: "Privacy Policy", href: "#" }
                        ])];
                        newLinks[idx] = { ...newLinks[idx], label: val };
                        updateField("footerLinks", newLinks);
                      }}
                      as="span"
                      placeholder="Link Label"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

function SocialIcon({ platform }: { platform: string }) {
  switch (platform) {
    case "facebook":
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      );
    case "twitter":
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );
    case "linkedin":
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      );
    case "instagram":
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
      );
    case "youtube":
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      );
    default:
      return <PiEnvelopeSimple className="w-4 h-4" />;
  }
}

export default HokFooter;
